/**
 * Invite User Dialog
 *
 * Sectioned invite flow matching the redesign prototype:
 *   - Contact (email)
 *   - Roles (clickable cards, multi-select; default [Clinician]) + per-role
 *     sub-blocks (co-sign when Clinician selected; prescribing-proxy when
 *     Operations Support selected, GDN-1119)
 *   - Tier & Add-ons (gated on Clinician role; uses compact TierTile +
 *     AddOnRow from `@features/subscriptions`)
 *   - Cost summary (inline box at bottom; reframes for trial / non-clinician)
 *
 * Backed by the post-GDN-1694 invite API: `roles: string[]`, optional
 * `needs_cosign`, optional `can_prepare_prescriptions_on_behalf` — the
 * deprecated singular `role` field is no longer sent. Client-side mirrors the
 * BE's `validate_role_combination` rule (billing_administrator is exclusive of
 * every other role).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BundleWithStatus } from '@features/subscriptions';
import {
  AddOnRow,
  formatPrice,
  getAddOnsIncludedInTier,
  getMonthlyAmountCents,
  TierTile,
  useBundleCatalog,
  useBundlingEnabled,
  useGetFullSubscriptionDetailsQuery,
} from '@features/subscriptions';
import { ScrollFade } from '@jotpsych/ui';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { useCreateInvitationMutation } from '../store/api';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableRoles?: ProviderRoleInfo[];
  /**
   * When true, hides per-month pricing from the footer button and reframes the
   * cost summary as "after trial" since the org won't be charged yet.
   */
  isTrialing?: boolean;
}

const DEFAULT_ROLES: ProviderRoleInfo[] = [
  { role: ROLES.CLINICIAN, display_name: 'Clinician', description: 'Clinical staff member' },
  {
    role: ROLES.OPERATIONS_SUPPORT,
    display_name: 'Operations Support',
    description: 'Operations team member',
  },
  {
    role: ROLES.PRACTICE_ADMINISTRATOR,
    display_name: 'Practice Administrator',
    description: 'Practice admin with billing access',
  },
  {
    role: ROLES.BILLING_ADMINISTRATOR,
    display_name: 'Billing Administrator',
    description: 'Billing-only access',
  },
  {
    role: ROLES.SUPERVISOR,
    display_name: 'Supervisor',
    description: 'Clinical supervisor',
  },
];

/**
 * Mirrors `Role.validate_role_combination` from
 * `services/ehr_api/.../platform/organizations/models/role.py` so users see
 * the rule at toggle time instead of post-submit 400. The card for any role
 * that would create an invalid combo with the current selection is disabled.
 */
function isRoleCardDisabled(role: string, selectedRoles: Set<string>): boolean {
  const hasBillingAdmin = selectedRoles.has(ROLES.BILLING_ADMINISTRATOR);
  const hasOther = [...selectedRoles].some(r => r !== ROLES.BILLING_ADMINISTRATOR);
  if (role === ROLES.BILLING_ADMINISTRATOR) {
    // Can't add Billing Administrator if any other role is selected.
    return hasOther && !selectedRoles.has(role);
  }
  // Any other role: can't be added if Billing Administrator is selected.
  return hasBillingAdmin && !selectedRoles.has(role);
}

function RoleCard({
  role,
  selected,
  onToggle,
  disabled,
}: {
  role: ProviderRoleInfo;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      onClick={() => !disabled && onToggle()}
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={e => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={theme => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        backgroundColor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: theme.transitions.create([
          'border-color',
          'background-color',
          'opacity',
        ]),
        '&:hover': disabled ? undefined : { borderColor: 'primary.light' },
      })}
    >
      <Checkbox checked={selected} disabled={disabled} size="small" sx={{ p: 0, mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {role.display_name || getRoleDisplayName(role.role)}
        </Typography>
        {role.description && (
          <Typography variant="caption" color="text.secondary">
            {role.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function InviteUserDialog({
  open,
  onClose,
  onSuccess,
  availableRoles = [],
  isTrialing: isTrialingProp,
}: InviteUserDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const [createInvitation, { isLoading }] = useCreateInvitationMutation();
  const { isBundlingEnabled: showBundling } = useBundlingEnabled();
  const {
    tiers,
    addOns,
    getCompatibleUserAddOnsForDisplay,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();

  const { data: subscriptionDetails } = useGetFullSubscriptionDetailsQuery();
  const isTrialing = isTrialingProp ?? !!subscriptionDetails?.is_trialing;
  const isAnnualBilling = subscriptionDetails?.billing_interval === 'year';

  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(
    () => new Set([ROLES.CLINICIAN]),
  );
  const [needsCosign, setNeedsCosign] = useState(false);
  const [canPrepareOnBehalf, setCanPrepareOnBehalf] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState<string>('');
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());

  const isClinician = selectedRoles.has(ROLES.CLINICIAN);
  const isOperationsSupport = selectedRoles.has(ROLES.OPERATIONS_SUPPORT);
  const canSelectBundles = showBundling && isClinician;
  const isSingleTier = tiers.length === 1;

  // Reset form when dialog opens — keeps residual state from a previous send
  // from leaking into the next open.
  useEffect(() => {
    if (open) {
      setEmail('');
      setSelectedRoles(new Set([ROLES.CLINICIAN]));
      setNeedsCosign(false);
      setCanPrepareOnBehalf(false);
      setSelectedTierId('');
      setSelectedAddOnIds(new Set());
    }
  }, [open]);

  // Clear role-gated flags when the gating role goes away — mirrors the BE
  // validation (cosign requires clinician; prescribing-proxy requires
  // operations_support). Prevents sending an invalid combo if a user toggles
  // a flag on then unchecks the gating role.
  useEffect(() => {
    if (!isClinician && needsCosign) setNeedsCosign(false);
  }, [isClinician, needsCosign]);
  useEffect(() => {
    if (!isOperationsSupport && canPrepareOnBehalf) setCanPrepareOnBehalf(false);
  }, [isOperationsSupport, canPrepareOnBehalf]);

  // Clear tier + add-ons when Clinician goes away — bundles only apply to
  // clinicians on the wire, no point keeping selection state for a role
  // change that invalidates them.
  useEffect(() => {
    if (!isClinician && (selectedTierId || selectedAddOnIds.size > 0)) {
      setSelectedTierId('');
      setSelectedAddOnIds(new Set());
    }
  }, [isClinician, selectedTierId, selectedAddOnIds.size]);

  // Single-tier orgs: auto-select the only tier so the add-ons list is
  // immediately interactive (matches the legacy behavior + saves a click).
  // Skipped if Clinician isn't in the role set since bundles wouldn't apply.
  useEffect(() => {
    if (canSelectBundles && isSingleTier && tiers[0] && !selectedTierId) {
      setSelectedTierId(tiers[0].bundle_id);
    }
  }, [canSelectBundles, isSingleTier, tiers, selectedTierId]);

  // Visible add-ons: union of compatible + grants-included (same pattern as
  // Person Card — lets tiers like Everything surface their grants-covered
  // add-ons as locked "Included" rows instead of hiding them).
  const visibleAddOns = useMemo(() => {
    const compatible = getCompatibleUserAddOnsForDisplay(selectedTierId || null);
    if (!selectedTierId) return compatible;
    const tier = tiers.find(t => t.bundle_id === selectedTierId);
    if (!tier) return compatible;
    const compatIds = new Set(compatible.map(a => a.bundle_id));
    const grantsIncluded = getAddOnsIncludedInTier(tier, addOns).filter(
      a => !compatIds.has(a.bundle_id),
    );
    return [...compatible, ...grantsIncluded];
  }, [getCompatibleUserAddOnsForDisplay, selectedTierId, tiers, addOns]);

  const includedAddOnIds = useMemo(() => {
    const tier = tiers.find(t => t.bundle_id === selectedTierId);
    if (!tier) return new Set<string>();
    return new Set(getAddOnsIncludedInTier(tier, addOns).map(a => a.bundle_id));
  }, [tiers, addOns, selectedTierId]);

  // Cost preview — tier monthly + paid add-ons (those not covered by tier
  // grants). Annual orgs see monthly equivalent.
  const cost = useMemo((): { cents: number; currency: string } => {
    if (!isClinician || !selectedTierId) return { cents: 0, currency: 'usd' };
    const tier = tiers.find(t => t.bundle_id === selectedTierId);
    if (!tier) return { cents: 0, currency: 'usd' };
    const memberAddOnBundles: BundleWithStatus[] = Array.from(selectedAddOnIds)
      .map(id => addOns.find(a => a.bundle_id === id))
      .filter((b): b is BundleWithStatus => b != null);
    const included = new Set(
      getAddOnsIncludedInTier(tier, memberAddOnBundles).map(a => a.bundle_id),
    );
    const paid = memberAddOnBundles.filter(a => !included.has(a.bundle_id));
    const tierCents = getMonthlyAmountCents(tier, isAnnualBilling);
    const addOnsCents = paid.reduce(
      (sum, a) => sum + getMonthlyAmountCents(a, isAnnualBilling),
      0,
    );
    return { cents: tierCents + addOnsCents, currency: tier.currency };
  }, [isClinician, selectedTierId, selectedAddOnIds, tiers, addOns, isAnnualBilling]);

  const handleRoleToggle = useCallback((role: string) => {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  }, []);

  const handleTierSelect = useCallback(
    (newTierId: string) => {
      setSelectedTierId(newTierId);
      // Drop add-ons no longer compatible OR now covered by the new tier's
      // grants. Belt-and-suspenders against double-billing.
      const compatible = new Set(
        getCompatibleUserAddOnsForDisplay(newTierId).map(a => a.bundle_id),
      );
      const tier = tiers.find(t => t.bundle_id === newTierId);
      const covered = tier
        ? new Set(getAddOnsIncludedInTier(tier, addOns).map(a => a.bundle_id))
        : new Set<string>();
      setSelectedAddOnIds(prev => {
        const filtered = new Set(
          [...prev].filter(id => compatible.has(id) && !covered.has(id)),
        );
        return filtered.size !== prev.size ? filtered : prev;
      });
    },
    [getCompatibleUserAddOnsForDisplay, tiers, addOns],
  );

  const handleToggleAddOn = useCallback((bundleId: string) => {
    setSelectedAddOnIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) next.delete(bundleId);
      else next.add(bundleId);
      return next;
    });
  }, []);

  const handleInvite = useCallback(async () => {
    if (!email.trim()) {
      enqueueSnackbar('Please enter an email address', { variant: 'warning' });
      return;
    }
    if (selectedRoles.size === 0) {
      enqueueSnackbar('Please select at least one role', { variant: 'warning' });
      return;
    }
    if (canSelectBundles && !selectedTierId) {
      enqueueSnackbar('Please select a tier', { variant: 'warning' });
      return;
    }

    // Belt-and-suspenders: never wire a tier-covered add-on to the BE (no
    // server-side guard exists; would create a redundant Stripe line item).
    const safeAddOnIds = Array.from(selectedAddOnIds).filter(
      id => !includedAddOnIds.has(id),
    );
    const bundleIds: string[] = canSelectBundles
      ? [selectedTierId, ...safeAddOnIds]
      : [];

    const rolesList = Array.from(selectedRoles);
    // Only send the gated flags when their role is in the selection. BE rejects
    // `needs_cosign=true` without clinician + `can_prepare_prescriptions_on_behalf=true`
    // without operations_support.
    const cosignPayload = isClinician ? { needs_cosign: needsCosign } : {};
    const proxyPayload = isOperationsSupport
      ? { can_prepare_prescriptions_on_behalf: canPrepareOnBehalf }
      : {};

    try {
      const result = await createInvitation({
        providerInvitationRequest: {
          email: email.trim(),
          roles: rolesList,
          ...(bundleIds.length > 0 && { bundle_ids: bundleIds }),
          ...cosignPayload,
          ...proxyPayload,
        },
      }).unwrap();

      clinicians.inviteSent({
        // `role` kept (set to roles[0]) so existing analytics dashboards that
        // key off the singular field don't break mid-migration. Move to
        // `roles[]` for new charts.
        role: rolesList[0],
        roles: rolesList,
        role_count: rolesList.length,
        has_tier: !!selectedTierId,
        tier_id: selectedTierId || undefined,
        addon_count: safeAddOnIds.length,
        addon_ids: safeAddOnIds,
        needs_cosign: isClinician ? needsCosign : undefined,
        can_prepare_prescriptions_on_behalf: isOperationsSupport
          ? canPrepareOnBehalf
          : undefined,
        success: true,
        was_resent: result.resent || false,
      });

      enqueueSnackbar(
        result.resent ? 'Invitation resent successfully' : 'Invitation sent successfully',
        { variant: 'success' },
      );
      onSuccess();
    } catch (error: unknown) {
      clinicians.inviteSent({
        role: rolesList[0],
        roles: rolesList,
        role_count: rolesList.length,
        has_tier: !!selectedTierId,
        tier_id: selectedTierId || undefined,
        addon_count: safeAddOnIds.length,
        addon_ids: safeAddOnIds,
        needs_cosign: isClinician ? needsCosign : undefined,
        can_prepare_prescriptions_on_behalf: isOperationsSupport
          ? canPrepareOnBehalf
          : undefined,
        success: false,
        was_resent: false,
      });
      const message =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
          ? String(error.data.message)
          : 'Failed to send invitation';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [
    email,
    selectedRoles,
    selectedTierId,
    selectedAddOnIds,
    canSelectBundles,
    isClinician,
    isOperationsSupport,
    needsCosign,
    canPrepareOnBehalf,
    includedAddOnIds,
    createInvitation,
    enqueueSnackbar,
    onSuccess,
    clinicians,
  ]);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  const rolesToDisplay = availableRoles.length > 0 ? availableRoles : DEFAULT_ROLES;

  const footerLabel = (() => {
    if (isLoading) return 'Sending…';
    if (isTrialing) return 'Send Invite';
    if (!isClinician) return 'Send Invite — $0/mo';
    return `Send Invite — ${formatPrice(cost.cents, cost.currency)}/mo`;
  })();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Invite Member
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTrialing
                ? "Add a new member to your practice. You won't be charged until your trial ends."
                : 'Add a new member to your practice.'}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isLoading}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}
      >
        <ScrollFade>
          <Box sx={{ px: 3, py: 3 }}>
            {/* Contact */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Contact
            </Typography>
            <TextField
              autoFocus
              type="email"
              label="Email Address"
              placeholder="clinician@example.com"
              fullWidth
              size="small"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
              onKeyDown={e => {
                if (e.key === 'Enter' && !isLoading) handleInvite();
              }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Roles */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Roles
            </Typography>
            <Stack spacing={1}>
              {rolesToDisplay.map(role => (
                <RoleCard
                  key={role.role}
                  role={role}
                  selected={selectedRoles.has(role.role)}
                  onToggle={() => handleRoleToggle(role.role)}
                  disabled={isLoading || isRoleCardDisabled(role.role, selectedRoles)}
                />
              ))}
            </Stack>
            {selectedRoles.has(ROLES.BILLING_ADMINISTRATOR) ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                Billing Administrator cannot be combined with other roles.
              </Typography>
            ) : null}

            {/* Co-sign sub-block — Clinician only */}
            {isClinician && (
              <Box sx={{ mt: 2, pl: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Switch
                    checked={needsCosign}
                    onChange={e => setNeedsCosign(e.target.checked)}
                    disabled={isLoading}
                    size="small"
                    sx={{ mt: -0.5 }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Require co-signature
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Notes must be co-signed by a supervisor before being finalized.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Prescribing-proxy sub-block — Operations Support only (GDN-1119) */}
            {isOperationsSupport && (
              <Box sx={{ mt: 2, pl: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Switch
                    checked={canPrepareOnBehalf}
                    onChange={e => setCanPrepareOnBehalf(e.target.checked)}
                    disabled={isLoading}
                    size="small"
                    sx={{ mt: -0.5 }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Prescribing Proxy
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Prep prescriptions on behalf of a prescriber. Requires JotRx on the
                      account.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Tier & Add-ons — gated on Clinician */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Tier & Add-ons
            </Typography>
            <Box
              sx={{
                opacity: isClinician ? 1 : 0.5,
                pointerEvents: isClinician ? 'auto' : 'none',
                transition: 'opacity 200ms',
              }}
            >
              {!isClinician && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Non-clinician roles are free. Add the Clinician role to select a tier and
                  add-ons.
                </Typography>
              )}

              {isCatalogLoading ? (
                <Grid container spacing={2}>
                  {[0, 1, 2].map(i => (
                    <Grid size={{ xs: 12, md: 4 }} key={i}>
                      <Skeleton variant="rounded" sx={{ height: 80 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    {tiers.map(tier => (
                      <Grid size={{ xs: 12, md: 4 }} key={tier.bundle_id}>
                        <TierTile
                          bundle={tier}
                          billingInterval={isAnnualBilling ? 'year' : 'month'}
                          isSelected={tier.bundle_id === selectedTierId}
                          onSelect={handleTierSelect}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {/*
                    Add-ons always render so the catalog is visible upfront
                    (matches the prototype); rows disable until a tier is
                    selected so the user can't toggle an add-on that doesn't
                    have a host plan yet.
                  */}
                  {visibleAddOns.length > 0 && (
                    <Stack spacing={1}>
                      {!selectedTierId && (
                        <Typography variant="caption" color="text.secondary">
                          Select a tier above to enable add-ons.
                        </Typography>
                      )}
                      {visibleAddOns.map(addOn => (
                        <AddOnRow
                          key={addOn.bundle_id}
                          bundle={addOn}
                          billingInterval={isAnnualBilling ? 'year' : 'month'}
                          isSelected={selectedAddOnIds.has(addOn.bundle_id)}
                          isIncluded={includedAddOnIds.has(addOn.bundle_id)}
                          onToggle={handleToggleAddOn}
                          disabled={!selectedTierId}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>

            {/* Cost summary */}
            <Box
              sx={theme => ({
                mt: 3,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: isClinician
                  ? alpha(theme.palette.primary.main, 0.06)
                  : alpha(theme.palette.grey[500], 0.08),
              })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {isTrialing ? 'Cost after trial' : 'Additional monthly cost'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: isClinician ? 'primary.main' : 'success.main',
                  }}
                >
                  {isClinician
                    ? `${formatPrice(cost.cents, cost.currency)}/mo`
                    : '$0/mo'}
                </Typography>
              </Box>
              {!isClinician && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Non-clinician roles have full access to their permissions at no additional cost.
                </Typography>
              )}
            </Box>
          </Box>
        </ScrollFade>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleInvite}
          disabled={
            isLoading ||
            !email.trim() ||
            selectedRoles.size === 0 ||
            (canSelectBundles && !selectedTierId)
          }
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {footerLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
